import {
	Combobox,
	ComboboxOption,
	ComboboxOptions,
	Popover,
	PopoverButton,
	PopoverPanel,
	Transition,
} from '@headlessui/react';
import React, { Fragment, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import FormButton from '@app/component/form/button/FormButton';
import { Controller, useForm } from 'react-hook-form';
import CompetitorTag from '@app/model/company_hotel/CompetitorTag';
import _isEmpty from 'lodash/isEmpty';
import Modal from '@app/component/overlays/modal/Modal';
import useModal from '@app/component/overlays/modal/ModalRef';
import SimpleColorPicker from '@app/component/form/color/SimpleColorPicker';
import FormInputText from '@app/component/form/input/text/FormInputText';
import { PencilIcon } from '@heroicons/react/24/solid';
import { PaletteColors } from '@app/component/form/color/PaletteColors';

// Props for the component
type InlineTagSelectorProps = {
	availableTags: CompetitorTag[];
	selectedTags: CompetitorTag[];
	onSelect: (tag: CompetitorTag) => void;
	onDeselect: (tag: CompetitorTag) => void;
	onCreate: (name: string, color: string) => Promise<CompetitorTag | void>;
	onEdit: (id: number, name: string, color: string) => Promise<CompetitorTag | void>;
	onDelete: (id: number) => Promise<CompetitorTag | void>;
	placeholder?: string;
};

const InlineTagSelector: React.FC<InlineTagSelectorProps> = ({
																															 availableTags,
																															 selectedTags,
																															 onSelect,
																															 onDeselect,
																															 onCreate,
																															 onEdit,
																															 onDelete,
																															 placeholder = 'Filter groups...',
																														 }) => {
	const [query, setQuery] = useState('');

	const safeAvailableTags = availableTags || [];

	const filteredTags =
		query === ''
			? safeAvailableTags
			: safeAvailableTags.filter((tag) =>
				tag.name.toLowerCase().includes(query.toLowerCase()),
			);

	const unselectedTags = !_isEmpty(filteredTags) ? filteredTags.filter(
		(tag) => !selectedTags.some((selected) => selected.id === tag.id),
	) : [];

	const hasSelections = !_isEmpty(availableTags);

	return (
		<div className="flex items-center gap-x-2">
			{selectedTags.map((tag) => (
				<span
					key={tag.id}
					style={{ backgroundColor: `${tag.color}20`, color: tag.color, borderColor: `${tag.color}80` }}
					className="inline-flex items-center gap-x-0.5 rounded-md px-2 py-1 text-xs font-medium"
				>
					<EditTagPopover tag={tag} onEdit={onEdit} onDelete={onDelete} />
					{tag.name}
					<button
						type="button"
						onClick={() => onDeselect(tag)}
						className="group relative -mr-1 w-3.5 h-3.5 rounded-sm"
					>
						<XMarkIcon className="w-3.5 h-3.5" />
					</button>
				</span>
			))}

			<Popover className="relative">
				{({ close: closeSelection }) => (
					<>
						<PopoverButton
							as="button"
							type="button"
							className="rounded-full bg-gray-200 p-0.5 text-gray-700 shadow-sm hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
						>
							<PlusIcon className="w-3.5 h-3.5" />
						</PopoverButton>

						<Transition
							as={Fragment}
							enter="transition ease-out duration-200"
							enterFrom="opacity-0 translate-y-1"
							enterTo="opacity-100 translate-y-0"
							leave="transition ease-in duration-150"
							leaveFrom="opacity-100 translate-y-0"
							leaveTo="opacity-0 translate-y-1"
						>
							<PopoverPanel anchor="bottom" transition
														className="z-10 mt-3 w-56 origin-top-right overflow-hidden rounded-md bg-white divide-y divide-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
								<div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
									<Combobox
										onChange={(tag: CompetitorTag) => {
											if (tag) {
												onSelect(tag);
											}
											closeSelection();
										}}
									>
										{/*{hasSelections && <div className="relative bg-white p-2">
											<Combobox.Input
												className="input"
												onChange={(event) => setQuery(event.target.value)}
												placeholder={placeholder}
											/>
										</div>}*/}
										<ComboboxOptions static
																		 className="max-h-60 w-full overflow-auto bg-white py-1 text-base focus:outline-none sm:text-sm">
											{unselectedTags.map((tag) => (
												<ComboboxOption
													key={tag.id}
													value={tag}
													className={({ focus }) =>
														`relative flex items-center px-4 py-2 text-xs text-gray-700 cursor-pointer ${
															focus ? ' text-gray-900' : 'text-gray-700'
														}`
													}
												>
													{({ selected }) => (
														<>
															<span className="w-2 h-2 mr-2 rounded-sm" style={{ backgroundColor: tag.color }} />
															<span
																className={`block truncate ${
																	selected ? 'font-medium' : 'font-normal'
																}`}
															>
															{tag.name}
														</span>
														</>
													)}
												</ComboboxOption>
											))}
										</ComboboxOptions>
										<div className={`"bg-white p-2 text-center ${hasSelections ? 'border-t border-gray-200' : ''}"`}>
											<CreateTagPopover onCreate={onCreate} />
										</div>
									</Combobox>
								</div>
							</PopoverPanel>
						</Transition>
					</>
				)}
			</Popover>
		</div>
	);
};

const CreateTagPopover: React.FC<{ onCreate: (name: string, color: string) => Promise<any> }> = ({ onCreate }) => {
	const modal = useModal();
	const form = useForm({
		defaultValues: {
			name: '',
			color: PaletteColors[0],
		},
	});
	const { control, handleSubmit, reset } = form;

	const handleCreate = (close: () => void) => handleSubmit(async (data) => {
		await onCreate(data.name, data.color);
		reset();
		close();
	});

	return <>
		<FormButton type="button" state="primary" size="small" onClick={modal.open} extraClassName="w-full">
			Create new
		</FormButton>
		<Modal ref={modal.ref} title="Create new group" actions={
			<FormButton type="submit" state="primary" onClick={handleCreate(modal.close)}>
				Create
			</FormButton>
		}>
			<form className="space-y-3">
				<div className="space-y-2">
					<FormInputText name="name" label="Name" form={form} placeholder="New group name" isRequired maxLength={60} />
					<div>
						<label className="block text-sm font-medium leading-6 text-gray-900">Color</label>
						<Controller
							name="color"
							control={control}
							render={({ field }) => (
								<SimpleColorPicker {...field} />
							)}
						/>
					</div>
				</div>
			</form>

		</Modal>
	</>;
};

const EditTagPopover: React.FC<{
	tag: CompetitorTag,
	onEdit: (id: number, name: string, color: string) => Promise<any>
	onDelete: (id: number) => Promise<any>
}> = ({ tag, onEdit, onDelete }) => {
	const modal = useModal();
	const form = useForm({
		defaultValues: {
			id: tag.id,
			name: tag.name,
			color: tag.color,
		},
	});
	const { control, handleSubmit, reset } = form;

	const handleEdit = (close: () => void) => handleSubmit(async (data) => {
		await onEdit(data.id!, data.name, data.color);
		reset();
		close();
	});

	const handleDelete = (close: () => void) => handleSubmit(async (data) => {
		await onDelete(data.id!);
		reset();
		close();
	});

	return <>
		<button
			type="button"
			onClick={modal.open}
			className="group relative -ml-1 mr-1 w-3 h-3 rounded-sm"
		>
			<PencilIcon className="w-3 h-3" />
		</button>
		<Modal ref={modal.ref} title={`Edit ${tag.name}`} actions={
			<>
				<FormButton state="primary" onClick={handleEdit(modal.close)}>
					Update
				</FormButton>
				<FormButton state="white" extraClassName="text-red-500" onClick={handleDelete(modal.close)}>
					Delete
				</FormButton>
			</>
		}>
			<form className="space-y-3">
				<div className="space-y-2">
					<FormInputText name="name" label="Name" form={form} placeholder="Group name" isRequired maxLength={60} />
					<div>
						<label className="block text-sm font-medium leading-6 text-gray-900">Color</label>
						<Controller
							name="color"
							control={control}
							render={({ field }) => (
								<SimpleColorPicker {...field} />
							)}
						/>
					</div>
				</div>
			</form>

		</Modal>
	</>;
};

export default InlineTagSelector;
